/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * Client-side mirror of the MacroExpression union type from the decorator
 * framework (libs/request-decorators/macro-expression.engine.ts).
 *
 * These types are kept in sync with the server-side definitions so that the
 * admin-console can author and persist decorator expressions without requiring
 * a direct dependency on the server-side library.
 */

/** A fixed literal string value. */
import type { DestinationFieldType } from '@destination-field';

export interface StaticExpression extends ExpressionDeclaration {
  type: 'static';
  value: string;
  /** Optional human-readable description of the expression's purpose. */
  description?: string;
}

/**
 * Suppresses the field entirely — the parameter/header is omitted rather than
 * set to an empty value.  Only valid as a `then` or `else` branch inside a
 * {@link ConditionalExpression}.
 */
export interface SuppressExpression extends ExpressionDeclaration {
  type: 'suppress';
  /** Optional human-readable description of the expression's purpose. */
  description?: string;
  /**
   * **What this suppression replaced, so that leaving it gives the value back** (`#1274`).
   *
   * The same control, under the same words, behaves differently on a row and on a branch: a row
   * keeps *never send it* in a slot **beside** the value, a branch keeps it **as** the value. So on
   * a branch the answer was destructive, and after `#542` it discards a whole calculation.
   *
   * `#1241`'s ratified answer, a level down — *a shape that declares no value may still transport
   * one, and both legs have to know*. The runtime reads `type` alone, so nothing here reaches an
   * exchange; `reshapeExpression` owns both legs. See the DTO for the full reasoning.
   */
  replaced?: Exclude<MacroExpression, SuppressExpression>;
}

/**
 * Optional regex extraction specification for {@link CalculatedExpression}.
 *
 * When present the resolved path value is matched against `pattern`.
 * The extracted token replaces the raw path value; a non-matching pattern
 * uses the expression's `default`.
 *
 * - `group` omitted or `0` → return full match (`match[0]`)
 * - `group: N` → return capture group N
 */
export interface RegexExtractionSpec {
  /** Regex pattern string (without leading/trailing `/` delimiters). */
  pattern: string;
  /** Optional flags, e.g. `'i'` for case-insensitive. */
  flags?: string;
  /**
   * Capture group index to return.
   * Defaults to `0` (full match) when omitted.
   */
  group?: number;
}

/**
 * A value resolved from the request context via a dot-notation path.
 *
 * @example { type: 'calculated', path: 'session.user.ipAddress', default: '0.0.0.0' }
 */
export interface CalculatedExpression extends ExpressionDeclaration {
  type: 'calculated';
  /**
   * Dot-notation path into the resolve context.
   * e.g. 'session.user.ipAddress', 'pod.sessionId', 'demandSource.name'
   */
  path: string;
  /**
   * Optional regex extraction applied to the resolved path value.
   *
   * When present the path value is matched against `regex.pattern`; the
   * extracted token (full match or a capture group) replaces the raw value.
   * `transform` is applied **after** regex extraction when both are set.
   */
  regex?: RegexExtractionSpec;
  /** Optional value transformation applied after resolution (and regex extraction) */
  transform?: 'lowercase' | 'uppercase' | 'trim';
  /** Value to use when the path cannot be resolved */
  default?: string;
  /** Pre-`#1216` spelling of `default` (`X3`), read so a stored expression resolves. */
  fallback?: string;
  /** Optional human-readable description of the expression's purpose. */
  description?: string;
}

// ---------------------------------------------------------------------------
// Computed expression types
// ---------------------------------------------------------------------------

/**
 * **What a `computed` expression does** — combine its operands, or produce a value from none.
 *
 * Mirrors `ComputedOperation` in `libs/dto/src/macro-expression.dto.ts`, where the reasoning is.
 * The short version: the built-ins were an operand *kind* until `E3`, which is `X39` one level
 * further down than it was written for — and `rnd`'s `min`/`max` have a home here and none in a
 * type-down.
 */
export type ComputedOperation =
  | 'concat'
  | 'add'
  | 'subtract'
  | 'multiply'
  | 'divide'
  | 'avg'
  | 'timestamp'
  | 'rnd'
  | 'uuid';

/**
 * **Whether this operation reads operands** — the operation's own declaration, in
 * `conditionRhsShape`'s shape. A producer's operands are kept and not rendered, so switching to
 * `timestamp` and back is an edit rather than a reset (`#1223`).
 */
export function operationArity(operation: ComputedOperation | undefined): 'combines' | 'produces' {
  return operation === 'timestamp' || operation === 'rnd' || operation === 'uuid' ? 'produces' : 'combines';
}

/** Pre-`E3` spelling of a {@link StaticExpression} operand. Read, never written. */
export interface LiteralOperand {
  type: 'literal';
  /** The constant value */
  value: string | number;
}

/** Pre-`E3` spelling of a {@link CalculatedExpression} operand. Read, never written. */
export interface PathOperand {
  type: 'path';
  /** Dot-notation path into the resolve context */
  path: string;
  /** Value to use when the path cannot be resolved */
  default?: string | number;
  /** Pre-`#1216` spelling of `default` (`X3`), read so a stored operand resolves. */
  fallback?: string | number;
}

/**
 * Pre-`E3` spelling of a built-in producer. Read, never written — the built-ins are
 * {@link ComputedOperation} members now.
 */
export interface FnOperand {
  type: 'fn';
  fn: 'timestamp' | 'rnd' | 'uuid';
  /** Minimum value for `rnd` (default: 0) */
  min?: number;
  /** Maximum value for `rnd` (default: 999999999) */
  max?: number;
}

/** The pre-`E3` operand spellings, read and never written. */
export type LegacyComputedOperand = LiteralOperand | PathOperand | FnOperand;

/**
 * **What may be *stored* in an operand slot** — mirror of `StoredComputedOperand`.
 *
 * Typed this wide so a direct read does not compile as though it were narrow: the sanctioned reader
 * is {@link readComputedOperands}, and nothing rewrites a stored document until it is next saved.
 */
export type StoredComputedOperand = ComputedOperand | LegacyComputedOperand;

/**
 * **A computed expression's operands, with the pre-`E3` spellings normalised** — the console's copy
 * of `readComputedOperands`, which is `X3`'s *one reader per store* rather than a duplicate: the
 * mirror declares these types by hand, so the normalisation lives beside them.
 *
 * `computed-operands-parity.test.ts` asserts this against the DTO's, the way
 * `macro-condition-operands.test.ts` does for the condition pair — `demand-source-mirror.spec.ts`
 * compares field **names**, not two functions agreeing.
 */
export function readComputedOperands(expression: ComputedExpression): ComputedOperand[] {
  return (expression.operands ?? []).map(readComputedOperand);
}

/** One operand, normalised — see {@link readComputedOperands}. */
export function readComputedOperand(operand: StoredComputedOperand): ComputedOperand {
  switch (operand.type) {
    case 'literal':
      return { type: 'static', value: String(operand.value) };
    case 'path': {
      const declared = operand.default ?? operand.fallback;
      return {
        type: 'calculated',
        path: operand.path,
        ...(declared !== undefined ? { default: String(declared) } : {}),
      };
    }
    case 'fn':
      return {
        type: 'computed',
        operation: operand.fn,
        operands: [],
        ...(operand.min !== undefined ? { min: operand.min } : {}),
        ...(operand.max !== undefined ? { max: operand.max } : {}),
      };
    default:
      return operand;
  }
}

/**
 * Combines multiple operands using a restricted set of operations.
 *
 * Operations:
 * - `concat`   (default) – stringify each operand and concatenate them
 * - `add`      – parse each operand as a number and sum
 * - `subtract` – subtract each subsequent operand from the first
 * - `multiply` – multiply all operands
 * - `divide`   – divide the first operand by each subsequent one
 *
 * @example Demand-source name + cache-buster
 * ```ts
 * {
 *   type: 'computed',
 *   operation: 'concat',
 *   separator: '_',
 *   operands: [
 *     { type: 'calculated', path: 'demandSource.name' },
 *     { type: 'computed',   operation: 'rnd', operands: [] },
 *   ],
 * }
 * // → "SpringServe_847392"
 * ```
 */
export interface ComputedExpression extends ExpressionDeclaration {
  type: 'computed';
  /**
   * Ordered list of operands — read through {@link readComputedOperands}, never directly.
   * A producer operation takes none, and the ones it was given are kept rather than discarded.
   */
  operands: StoredComputedOperand[];
  /** How to combine the operands, or what to produce without them (default: `'concat'`). */
  operation?: ComputedOperation;
  /** Minimum for the `rnd` operation (default: 0). Meaningless for every other operation. */
  min?: number;
  /** Maximum for the `rnd` operation (default: 999999999). Meaningless for every other operation. */
  max?: number;
  /** Separator between operands for `concat` operations (default: `''`). */
  separator?: string;
  /** Default used when the computation fails (`''` when absent). */
  default?: string;
  /** Pre-`#1216` spelling of `default` (`X3`), read so a stored expression resolves. */
  fallback?: string;
  /** Optional human-readable description of the expression's purpose. */
  description?: string;
}

// ---------------------------------------------------------------------------
// Conditional expression types
// ---------------------------------------------------------------------------

/**
 * A single comparison — the **leaf** of a {@link CriteriaNode} predicate.
 */
/**
 * **An operand is a value** — either side of a comparison, and every operand of a calculation.
 *
 * Mirrors `ValueOperand` in `libs/dto/src/macro-expression.dto.ts`, which carries the reasoning. The
 * value model minus `suppress` (an answer about the wire, not a value) and `conditional` (a decision
 * where a value is wanted, and undesigned inside a calculation — `#1272` measured a *branch*'s depth,
 * which is a different slot).
 *
 * **`shared` came in with `#1295`.** It was excluded because *nothing could be pointed at*, `XS5`
 * retired that the day before, and `E4` lifted the same exclusion for a branch the next morning — the
 * operand was the uncollected half of one expiry. It was `ConditionOperand` until `E3` gave a
 * calculation's operands the same shapes for the same reasons; one concept, one name, at the level
 * that holds it.
 */
export type ValueOperand =
  | StaticExpression
  | CalculatedExpression
  | ComputedExpression
  | ReferenceExpression
  | MacroTokenExpression
  | BoundValueExpression;

/**
 * **What a calculation's operand may be: a value, or each member of a list** — mirror of
 * `ComputedOperand` in `libs/dto/src/macro-expression.dto.ts` (`CO8`, `2e`, `#1369`), where the
 * reasoning is. A calculation's operand only: not a condition's, and not a top-level value until `4f`.
 */
export type ComputedOperand = ValueOperand | CollectionOperand;

/**
 * **Each member of a list** — mirror of `CollectionOperand`. Authored as `path[*].member`, the same
 * `[*]` the ORTB body already uses for *every element* (RS4c), and stored as the two paths.
 */
export interface CollectionOperand {
  type: 'collection';
  path: string;
  member?: string;
}

/**
 * **The operator vocabulary** — mirror of `libs/dto/src/macro-expression.dto.ts`.
 *
 * Asserted against the DTO's from both directions: `demand-source-mirror.spec.ts` compares the
 * declared unions member for member, and `condition-operator-vocabulary.test.ts` compares this
 * array against the DTO's **and** against what the editor's dropdown offers — which is the third of
 * `#1196`'s four homes and the one no spec could see before `#1287`.
 */
export type ConditionOperator =
  | 'exists'
  | 'notExists'
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'contains'
  | 'matches'
  | 'isAnyOf';

/** The vocabulary as a set, in declaration order — which is the order the editor offers it in. */
export const CONDITION_OPERATORS: readonly ConditionOperator[] = [
  'exists',
  'notExists',
  'eq',
  'neq',
  'gt',
  'gte',
  'lt',
  'lte',
  'contains',
  'matches',
  'isAnyOf',
];

/** Pre-`#1287` spelling of `isAnyOf`, read and never written — the reasoning is on the DTO. */
export type LegacyConditionOperator = 'isOneOf';

/**
 * What may be **stored** in `MacroCondition.operator`. Typed wider than the vocabulary so a direct
 * read does not compile as though it were narrow: handing `'isOneOf'` to {@link conditionRhsShape}
 * would render a value box over an authored list.
 */
export type StoredConditionOperator = ConditionOperator | LegacyConditionOperator;

/**
 * **The operator this condition is written in, with the pre-`#1287` spelling normalised** — the
 * console's copy of `conditionOperator`, `X3`'s *one reader per store*.
 */
export function conditionOperator(condition: Pick<MacroCondition, 'operator'>): ConditionOperator {
  return condition.operator === 'isOneOf' ? 'isAnyOf' : condition.operator;
}

/**
 * Which shape this operator's right-hand side takes — `X10`, applied to an operator.
 *
 * `contains` stays `scalar` though it now reads a list (`#1287`): its slot is a full value line and
 * a literal list there is authored as `all` of N rows. The reasoning is on the DTO.
 */
export function conditionRhsShape(operator: ConditionOperator): 'none' | 'list' | 'scalar' {
  if (operator === 'exists' || operator === 'notExists') return 'none';
  if (operator === 'isAnyOf') return 'list';
  return 'scalar';
}

/**
 * **The operands of a condition, with the pre-`E2b` spellings normalised** — the console's copy of
 * `readConditionOperands`, which is `X3`'s *one reader per store* rather than a duplicate: the
 * mirror declares these types by hand, so the normalisation lives beside them.
 *
 * `demand-source-mirror.spec.ts` cannot see this pair — it compares field **names** and
 * string-literal unions, not two functions agreeing — so `macro-condition-operands.test.ts` asserts
 * this one against the same table the driver's is asserted against.
 */
export function readConditionOperands(condition: MacroCondition): {
  /** The operator, with the pre-`#1287` spelling normalised — {@link conditionOperator}. */
  operator: ConditionOperator;
  left: ValueOperand | undefined;
  right: ValueOperand | undefined;
  values: (string | number | boolean)[] | undefined;
} {
  const operator = conditionOperator(condition);
  const left: ValueOperand | undefined =
    condition.left ?? (condition.path !== undefined ? { type: 'calculated', path: condition.path } : undefined);

  const shape = conditionRhsShape(operator);
  if (shape === 'none') return { operator, left, right: undefined, values: undefined };

  const asPath: ValueOperand | undefined =
    condition.right ??
    (condition.valuePath !== undefined ? { type: 'calculated', path: condition.valuePath } : undefined);

  if (shape === 'list') {
    const stored = condition.values ?? condition.value;
    const values = stored === undefined ? undefined : Array.isArray(stored) ? stored : [stored];
    // A list operator has both slots: `values` is the authored literal list, `right` a reference to
    // one. Dropping the second was this change's one runtime regression, caught by the engine spec.
    return { operator, left, right: asPath, values };
  }

  // A stored literal that is a **list** stays a list (`#1287`): `String(['US','CA'])` is `'US,CA'`,
  // which is the defect this issue found in the engine arriving one layer above it. The reasoning is
  // on the DTO; the control shape is a separate question and stays `scalar`.
  const literalIsList = Array.isArray(condition.value);
  const right: ValueOperand | undefined =
    asPath ??
    (condition.value !== undefined && !literalIsList ? { type: 'static', value: String(condition.value) } : undefined);
  return {
    operator,
    left,
    right,
    values: literalIsList ? (condition.value as (string | number | boolean)[]) : undefined,
  };
}

export interface MacroCondition {
  /** The value being tested — `X50`. `path` is its pre-`E2b` spelling, read and never written. */
  left?: ValueOperand;
  /**
   * **Names the resolved `left`, so a later slot can read it** (`X55b`, `S3b`). Mirrors
   * `libs/dto/src/macro-expression.dto.ts`, which carries the reasoning: a generated id, written when
   * a slot first reads the tested value, visible in this case's outcome and everything after it.
   */
  bindAs?: string;
  /** The value compared against, for the operators that take a scalar, or a reference to a list. */
  right?: ValueOperand;
  /** The authored literal list, for the operators that take one — today `isAnyOf`. */
  values?: (string | number | boolean)[];
  /** Dot-notation path to the value to test */
  path?: string;
  /**
   * Comparison operator. **Read through {@link conditionOperator}, never off this field.**
   *
   * **`isOneOf` reached the DTO with `#1069` and this mirror did not follow**, so the console
   * could neither offer nor round-trip the operator every shorthand desugars into — and Todd's
   * own worked example for `XS1` (*"genre is one of …"*) was unauthorable. Kept honest by
   * `scripts/demand-source-mirror.spec.ts`, which now compares this union against the DTO's.
   *
   * It is `isAnyOf` since `#1287`, which widened its left side to a list; `isOneOf` is the legacy
   * spelling. The pair is *`contains` = all · `isAnyOf` = any* and the reasoning is on the DTO.
   */
  operator: StoredConditionOperator;
  /**
   * Right-hand value for comparison operators (static literal).
   *
   * An **array** for `isAnyOf`, which is the list the left-hand side must have a member of. A
   * non-array is read as a one-member list rather than matching nothing.
   */
  value?: string | number | boolean | Array<string | number | boolean>;
  /**
   * Right-hand context path for comparison operators.
   * When set, the comparison RHS is resolved from the context instead of
   * using the static `value` field.  Takes precedence over `value`.
   * @example `pod.maxFetchRounds`
   */
  valuePath?: string;
  /**
   * **Compare text exactly, including case.** Absent means *fold case*, which is the default
   * (`#1200`). Written only when `true`, so a stored condition needs no migration.
   *
   * The reasoning is on the DTO. The half that matters here: the row must **say which mode it is
   * in**, because the operator cannot otherwise find out without running a test — which is how
   * this was reported in the first place.
   */
  caseSensitive?: true;
}

/**
 * A node of a predicate tree — mirror of `libs/dto/src/criteria.ts`.
 *
 * **A tree in the model, one level in the editor** (`X2`, `XS1`). The builder authors a flat
 * `all`/`any` list of conditions, which *is* a tree of depth one; keeping the tree costs nothing
 * today and means nesting later arrives as an editor change rather than a change to the stored
 * shape and every reader of it.
 */
export type CriteriaNode =
  | { type: 'condition'; condition: MacroCondition }
  | { type: 'all'; of: CriteriaNode[] }
  | { type: 'any'; of: CriteriaNode[] }
  | { type: 'not'; of: CriteriaNode };

const CRITERIA_NODE_TYPES = new Set(['condition', 'all', 'any', 'not']);

/**
 * **Read a predicate that may still be a bare {@link MacroCondition}** — mirror of
 * `toCriteriaNode` in `libs/dto/src/criteria.ts`, and every console read of
 * `ConditionalExpression.condition` goes through it.
 *
 * A stored bare condition exists: the trybidbrain.com backup of 2026-08-26 carries one
 * (`regs.coppa`), which the design pass had recorded as impossible. Untolerated it would not
 * error — the editor would render an empty condition list and **save the operator's rule away**.
 */
export function toCriteriaNode(node: CriteriaNode | MacroCondition): CriteriaNode {
  const type = (node as { type?: unknown }).type;
  if (typeof type === 'string' && CRITERIA_NODE_TYPES.has(type)) return node as CriteriaNode;
  return { type: 'condition', condition: node as MacroCondition };
}

/**
 * Evaluates a predicate and branches to `then` or `else`.
 *
 * @example
 * {
 *   type: 'conditional',
 *   condition: { type: 'any', of: [
 *     { type: 'condition', condition: { path: 'session.privacy.coppa', operator: 'exists' } },
 *   ] },
 *   then:  { type: 'static', value: '1' },
 *   else:  { type: 'static', value: '0' },
 * }
 */
/**
 * **What a conditional's `then` or `else` may hold — every shape there is** (`E4`, `#1272`).
 *
 * There used to be a narrowing here, and it is gone rather than widened. `E1` took it from
 * `Static | Calculated | Suppress` to include `computed`; `E4` measured what was left — `conditional`
 * (**bounded**, the recursion) and `reference` (**chosen**, Todd 2026-09-08: *not MVP, not until
 * requested*) — and both fell:
 *
 * - **`conditional`** is the recursion, and its limit is *how deep a reader can follow a decision*,
 *   not what a document may store. That is a **rendering** bound and it now lives where it can be
 *   rendered — `MAX_CONDITIONAL_NESTING` in `case-ladder.model.ts`. `X42` said this from the start:
 *   the narrowing is a rendering requirement rather than a type widening.
 * - **`reference`** was lifted by Todd 2026-09-10, because `E4` is the request that deferral was
 *   waiting for: the refusal above the bound has to say *author it as a shared expression and
 *   address it here*, and it may not point at a chip the same branch refuses.
 *
 * So a branch holds a `MacroExpression`, exactly as `libs/dto/src/macro-expression.dto.ts` has always
 * declared. **A mirror that is narrower than the DTO is invisible to `demand-source-mirror.spec.ts`**
 * — it compares field names and string-literal unions, not a field whose type is narrower — which is
 * how the pre-`E1` narrowing survived from March to September (`#1202`). Nothing narrower is
 * reintroduced here; a limit on what an operator may *author* is a limit the editor states and the
 * operator can see.
 */
export interface ConditionalExpression extends ExpressionDeclaration {
  type: 'conditional';
  condition: CriteriaNode;
  /**
   * The outcome when the condition holds — **or a decision of its own** (`E4`, `#1272`).
   *
   * `then` and `else` now hold the same type and still nest for two different reasons, which is the
   * whole of `X41` and survives the widening:
   *
   * | | Is | Bounded by |
   * | --- | --- | --- |
   * | **`else` nests** | the **case ladder** — *else if … else if … else* — which the engine has always resolved as first-match-wins, and which `flattenCaseLadder` renders flat so the order that decides it is visible | nothing. A ladder is as long as the operator needs |
   * | **`then` nests** | the **recursion** — an outcome that is itself a decision | `MAX_CONDITIONAL_NESTING`, and it bounds the **editor** rather than the document |
   *
   * The distinction is why the ladder projection walks `else` and renders `then`: flattening the
   * recursion would put two decisions on one line and lose which one is asking.
   */
  then: MacroExpression;
  /**
   * The outcome when the condition does not hold — **or the next rung of a case ladder**.
   *
   * Absent is a third state and is never invented (`#1251`): a conditional with no `else` resolves
   * to nothing, which on an ORTB body leaf is the only spelling of *accept the driver's own value*.
   */
  else?: MacroExpression;
  /** Optional human-readable description of the expression's purpose. */
  description?: string;
}

/**
 * **A macro, stored as an expression** (`X58`, `#1428`) — mirrors `libs/dto/src/macro-expression.dto.ts`,
 * which carries the reasoning. A `{macro}` on any mapping surface is this node; on screen it is still
 * `X27`'s *macro → the path it reaches*.
 */
export interface MacroTokenExpression extends ExpressionDeclaration {
  type: 'macro';
  /** The macro's name, without its delimiters. */
  name: string;
  /** Sent when the macro resolves to nothing. Blank means send nothing. */
  default?: string;
  /** Optional human-readable description of the expression's purpose. */
  description?: string;
}

/**
 * **What an expression declares about itself, whatever its shape** — mirrors
 * `libs/dto/src/macro-expression.dto.ts`, which carries the reasoning (`FD11` §5, `4e`, `#1415`).
 *
 * `asType` is **what the expression yields**, authored in the shared expression editor and
 * overridden by a row's own `Type`. Absence on the row means *use this*. `@expression-yield` is where
 * every reader gets the answer, so a console copy of the precedence cannot disagree with the driver's.
 */
export interface ExpressionDeclaration {
  asType?: DestinationFieldType;
}

/**
 * Union of all supported macro expression types that can be authored in the
 * admin-console editor.
 */
export type MacroExpression =
  | StaticExpression
  | CalculatedExpression
  | ComputedExpression
  | ConditionalExpression
  | SuppressExpression
  | ReferenceExpression
  | MacroTokenExpression
  | BoundValueExpression;

/**
 * **A named expression, addressed rather than copied** (`X5`, `XS5`, `#1066`).
 *
 * Mirrors `libs/dto/src/macro-expression.dto.ts`, which carries the reasoning — including why this
 * is a member of the union rather than a path (a path cannot carry a suppression) and why it is
 * resolved at the config read boundary rather than in a driver.
 *
 * **The console is the side that keeps the address.** `ConfigService.get` serves authoring and hands
 * back the reference; only `getForRuntime` substitutes the expression. So every surface here renders
 * *from &lt;name&gt;* over the expression it names, and the row's own `default` is the local override
 * (`X36`) rather than the only copy.
 */
export interface ReferenceExpression extends ExpressionDeclaration {
  type: 'reference';
  /** The referenced expression's artifact id — minted on creation and stable across a rename. */
  ref: string;
  /**
   * **The local override** (`X36`), written only when the operator changes it: absence means *use
   * the expression's own default*, never *there is none*.
   */
  default?: string;
  /** Optional human-readable description of the expression's purpose. */
  description?: string;
  /**
   * **The referenced expression's own version, as this row last recorded it** (`#1302`).
   *
   * Mirrors `libs/dto/src/macro-expression.dto.ts`, which carries the reasoning — including why the
   * value is the entry's `updatedAt` rather than the row's save time, why absence means *nobody
   * recorded a version* rather than *unchanged*, and why it is compared for inequality.
   */
  seenAt?: string;
}

/**
 * **The value an earlier condition tested, read by name** (`X55b`, `S3b`, `#1247` gap 3).
 *
 * Mirrors `libs/dto/src/macro-expression.dto.ts`, which carries the reasoning — a new kind of address
 * that resolves against **the expression it is inside** rather than a context, and a scope that is
 * the case's outcome and every later case and *Otherwise*. The console labels it by where it is
 * declared (`case-ladder.model.ts`), never by its stored name.
 */
export interface BoundValueExpression extends ExpressionDeclaration {
  type: 'bound';
  /** The `bindAs` it reads — a generated id. */
  name: string;
  /** Optional human-readable description of the expression's purpose. */
  description?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Create a default empty static expression. */
export function defaultStaticExpression(value = ''): StaticExpression {
  return { type: 'static', value };
}

/** Create a default calculated expression. */
export function defaultCalculatedExpression(path = ''): CalculatedExpression {
  return { type: 'calculated', path };
}

/**
 * **An operation that combines opens with two operands** (`X53`, `#1284`) — Todd, on `E3`'s
 * rehearsal: *"when adding an operation that expects two or more operands, we should add two
 * operands (the one the user clicked calculate on like now, plus an empty one to apply) — we offer a
 * similar behavior when choosing `random`, it adds the fields it expects."*
 *
 * **{@link operationArity} already answers the number, so nothing new is declared.** Every combining
 * operation expects two or more and every producer expects none; there is no operation that takes
 * exactly one or exactly three. Asking the vocabulary for a *count* would be a second declaration
 * with one distinction in it, free to disagree with the kind — the shape `#1269` is about.
 *
 * It pads at the moments the operator **acts** — reshaping a value into a calculation, or choosing
 * an operation that reads operands — and never on render. A stored one-operand calculation is
 * something an author wrote; growing it while they look at it would be a write on read, and a
 * one-operand `concat` resolves to its operand exactly as it always did.
 */
export function openingOperands(
  operation: ComputedOperation | undefined,
  operands: readonly ComputedOperand[],
): ComputedOperand[] {
  if (operationArity(operation) === 'produces') return [...operands];
  // A list is already many values (`CO8`, `2e`): `avg` over each cached bid is complete with one
  // operand, and padding it with an empty row would fold a `0` into the average.
  if (operands.some((operand) => operand.type === 'collection')) return [...operands];
  const padded = [...operands];
  while (padded.length < 2) padded.push(defaultValueOperand());
  return padded;
}

/** Create a default computed expression, opened to the operand count its operation reads. */
export function defaultComputedExpression(): ComputedExpression {
  return {
    type: 'computed',
    operation: 'concat',
    operands: openingOperands('concat', []),
  };
}

/**
 * **An operand's starting shape, and it is a path** — the same answer `defaultConditionalExpression`
 * gives its `then`, because an operator who adds an operand is far likelier to be reaching for a
 * session value than for a constant, and typing a constant into a path field reclassifies it as a
 * literal on the keystroke (`X24`).
 */
export function defaultValueOperand(): ValueOperand {
  return { type: 'calculated', path: '' };
}

/** Create a default empty condition leaf. */
export function defaultMacroCondition(): MacroCondition {
  return { operator: 'exists', path: '' };
}

/**
 * Create a default conditional expression.
 *
 * **Always an `all`/`any` list, even at one condition.** The alternative — a bare `condition`
 * node until a second is added — loses the operator's `any` the moment they delete back down to
 * one, and makes every reader branch on the arity. A one-element `all` is exactly as valid.
 */
export function defaultConditionalExpression(): ConditionalExpression {
  return {
    type: 'conditional',
    condition: { type: 'all', of: [{ type: 'condition', condition: defaultMacroCondition() }] },
    then: { type: 'calculated', path: '' },
    else: { type: 'static', value: '' },
  };
}

/** Create a default suppress expression. */
export function defaultSuppressExpression(): SuppressExpression {
  return { type: 'suppress' };
}

/** Return a human-readable label for an expression type. */
export function expressionTypeLabel(type: MacroExpression['type']): string {
  switch (type) {
    case 'static':
      return 'Static';
    case 'calculated':
      return 'Dynamic';
    case 'computed':
      return 'Calculated';
    case 'conditional':
      return 'Conditional';
    case 'suppress':
      return 'Suppress';
    default:
      return 'Unknown';
  }
}

/** Return a short badge colour token for each expression type. */
export function expressionTypeBadgeColor(type: MacroExpression['type']): string {
  switch (type) {
    case 'static':
      return '#19155E';
    case 'calculated':
      return '#5BA411';
    case 'computed':
      return '#683CD8';
    case 'conditional':
      return '#B85C00';
    case 'suppress':
      return '#888';
    default:
      return '#555';
  }
}
